import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
} from "react";
import { jwtDecode } from "jwt-decode";
import { getMe } from "../../utils/services/modules/login"; // ajuste esse caminho se necessário

interface IContacts {
  name: string;
  avatar: string;
  code: number;
}

interface User {
  userName: string;
  name: string;
  coins: number;
  code: number;
  avatar: string;
  cellphone: string;
  isAdmin: boolean;
  token: string;
  contacts: IContacts[];
}

interface UserState {
  user: User | null;
  loading: boolean;
}

const initialUserState: UserState = {
  user: null,
  loading: false,
};

type UserAction =
  | { type: "SET_USER"; payload: User }
  | { type: "CLEAR_USER" }
  | { type: "UPDATE_COINS"; payload: { coins: number } }
  | { type: "UPDATE_CONTACTS"; payload: IContacts[] }
  | { type: "UPDATE_PROFILE"; payload: { name: string; cellphone: string } }
  | { type: "SET_LOADING"; payload: boolean };

function userReducer(state: UserState, action: UserAction): UserState {
  switch (action.type) {
    case "SET_USER":
      return { ...state, user: action.payload };
    case "CLEAR_USER":
      return { ...state, user: null };
    case "UPDATE_COINS":
      if (!state.user) return state;

      return {
        ...state,
        user: {
          ...state.user,
          coins: action.payload.coins,
        },
      };
    case "UPDATE_CONTACTS":
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          contacts: action.payload,
        },
      };
    case "UPDATE_PROFILE":
      if (!state.user) return state;
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    default:
      return state;
  }
}

const UserContext = createContext<
  { state: UserState; dispatch: React.Dispatch<UserAction> } | undefined
>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(userReducer, initialUserState);

  useEffect(() => {
    const loadUserFromToken = async () => {
      dispatch({ type: "SET_LOADING", payload: true });

      const token = localStorage.getItem("token");
      if (!token) {
        dispatch({ type: "SET_LOADING", payload: false });
        return;
      }

      try {
        const decoded: { exp: number } = jwtDecode(token);
        const now = Math.floor(Date.now() / 1000);

        if (decoded.exp < now) {
          localStorage.removeItem("token");
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }

        const data = await getMe(token);

        dispatch({
          type: "SET_USER",
          payload: {
            name: data?.user.name,
            userName: data?.user.userName,
            coins: data?.user.coins,
            code: data?.user.code,
            avatar: data?.user.avatar,
            cellphone: data?.user.cellphone,
            isAdmin: data?.user.isAdmin,
            contacts: data?.user.contacts,
            token: token,
          },
        });
      } catch (error) {
        console.error("Erro ao carregar usuário do token:", error);
        localStorage.removeItem("token");
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    loadUserFromToken();
  }, []);

  return (
    <UserContext.Provider value={{ state, dispatch }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
